import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'rexue');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = getCategoryBySlug(activeSlug);
  const comics = getComicsByCategory(activeSlug);

  const sortedComics = [...comics].sort((a, b) => {
    if (sortBy === 'latest') return new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime();
    if (sortBy === 'popular') return b.views - a.views;
    return b.rating - a.rating;
  });

  useEffect(() => {
    document.title = `${currentCategory?.name || '分类'}韩漫 - 韩国漫画大全 | 免费在线韩漫阅读`;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug, currentCategory]);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || ''}韩漫 - 韩国漫画大全`,
    description: currentCategory?.description || '韩国漫画分类浏览',
    url: typeof window !== 'undefined' ? window.location.href : '',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩国漫画大全',
      url: typeof window !== 'undefined' ? window.location.origin : ''
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <main>
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="px-4 md:px-6 py-8">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { name: '分类浏览', path: '/category/rexue' },
                  { name: currentCategory?.name || '' }
                ]}
              />

              <div className="mt-4">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  {currentCategory?.name}韩漫
                </h1>
                <p className="text-sm text-foreground-500 mt-2 max-w-2xl">
                  {currentCategory?.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/70">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto py-3 overflow-x-auto">
              <div className="flex items-center gap-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveSlug(cat.slug)}
                    className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-950">{sortedComics.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-2">
                {[
                  { value: 'latest', label: '最新' },
                  { value: 'popular', label: '最热' },
                  { value: 'rating', label: '评分' }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value as typeof sortBy)}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {sortedComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {sortedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无作品，敬请期待</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}